import { useCallback, useEffect, useRef } from 'react'
import { useVideoPlaybackStore } from '../stores/videoPlaybackStore'

/** A caption track after `resolveVideoMedia` normalization. */
export interface ResolvedCaptionTrack {
  url: string
  lang: string
  label: string
  kind: 'subtitles' | 'captions'
}

export interface VideoCaptionsState {
  /** False when the media has no tracks: the Captions control is hidden. */
  hasCaptions: boolean
  /** The viewer's global captions preference. */
  enabled: boolean
  toggle: () => void
}

/** Label + language: how a rendered `<track>` shows up in `textTracks`. */
function sidecarKey(label: string, language: string): string {
  return `${label}\u0000${language}`
}

/**
 * The `TextTrack`s this player rendered as `<track>` children, in render order
 * (so the first is `tracks[0]`). Uses each element's `.track` where the
 * browser exposes it and falls back to a label + language match otherwise.
 */
function ownedTextTracks(
  video: HTMLVideoElement,
  textTracks: TextTrackList,
  tracks: ResolvedCaptionTrack[],
): TextTrack[] {
  const fromElements: TextTrack[] = []
  for (const el of Array.from(video.querySelectorAll('track'))) {
    if (el.track) fromElements.push(el.track)
  }
  if (fromElements.length > 0) return fromElements

  const byKey = new Map<string, TextTrack>()
  for (let i = 0; i < textTracks.length; i += 1) {
    const textTrack = textTracks[i]
    const key = sidecarKey(textTrack.label, textTrack.language)
    if (!byKey.has(key)) byKey.set(key, textTrack)
  }
  const owned: TextTrack[] = []
  for (const track of tracks) {
    const textTrack = byKey.get(sidecarKey(track.label, track.lang))
    if (textTrack && !owned.includes(textTrack)) owned.push(textTrack)
  }
  return owned
}

/**
 * Keeps one player's sidecar text tracks in line with the global captions
 * preference: our first track is `showing` while captions are on, and every
 * track we rendered is `disabled` otherwise (so the browser does not even
 * fetch the VTT until the viewer asks for captions).
 *
 * Tracks are matched by identity, not position: Safari's native HLS can list
 * the manifest's own text tracks ahead of our `<track>` children. Tracks this
 * player did not render are left untouched.
 */
export function useVideoCaptions(
  video: HTMLVideoElement | null,
  tracks: ResolvedCaptionTrack[],
): VideoCaptionsState {
  const enabled = useVideoPlaybackStore((s) => s.captionsEnabled)
  const hasCaptions = tracks.length > 0
  // `tracks` is rebuilt on every render upstream, so key the effect on its
  // content and read the latest array through a ref.
  const tracksKey = tracks.map((t) => `${t.url}|${t.lang}|${t.label}`).join('\n')
  const tracksRef = useRef(tracks)
  tracksRef.current = tracks

  useEffect(() => {
    // jsdom and some embedded webviews have no TextTrackList.
    const textTracks = video?.textTracks
    if (!video || !textTracks) return

    const apply = () => {
      ownedTextTracks(video, textTracks, tracksRef.current).forEach((textTrack, i) => {
        const mode: TextTrackMode = enabled && i === 0 ? 'showing' : 'disabled'
        if (textTrack.mode !== mode) textTrack.mode = mode
      })
    }
    apply()
    // <track> children register asynchronously, and a source swap re-adds them.
    textTracks.addEventListener?.('addtrack', apply)
    return () => textTracks.removeEventListener?.('addtrack', apply)
  }, [video, enabled, tracksKey])

  const toggle = useCallback(() => {
    const store = useVideoPlaybackStore.getState()
    store.setCaptionsEnabled(!store.captionsEnabled)
  }, [])

  return { hasCaptions, enabled, toggle }
}
